import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import Layout from 'components/common/layout/Layout';
import SearchFilter from 'components/common/forms/SearchFilter';
import Paging from 'components/common/nav/Paging';
import Footer from 'components/common/layout/Footer';
import TableForm from 'components/common/lists/TableForm';
import ChangeChartName from 'pages/modal/ChangeChartName';
import RegisterPracticeChart from 'pages/modal/RegisterPracticeChart';
import styles from 'styles/components/common/lists/Table.module.scss';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron_right.svg';
import PenIcon from 'assets/icons/pen_purple.png';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { DEFAULT_PAGE_SIZE } from 'constants/page';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { TTableColumn } from 'types/components/table';
import {
  PracticeChartSortLabelType,
  PracticeChartTableFormLabel,
} from 'types/form/practiceChart';
import {
  PracticeChartTableHeader,
  PracticeChartTableSortingLabel,
  TPracticeChartListParams,
  TPracticeChartTableInfo,
} from 'types/apis/chart/studentPracticeChart';
import { SortDirection } from 'types/apis/common';
import { getPracticeChartList } from 'apis/chart/studentPracticeChart';
import useModal from 'hooks/useModal';

const tableColumns: TTableColumn[] = [
  {
    name: 'no',
    label: '번호',
    width: '70px',
  },
  {
    name: PracticeChartTableHeader.ChartType,
    label: PracticeChartTableFormLabel.ChartType,
    width: '290px',
  },
  {
    name: PracticeChartTableHeader.ChartName,
    label: PracticeChartTableFormLabel.ChartName,
    width: '350px',
    onClick: () => {
      return;
    },
  },
  {
    name: PracticeChartTableHeader.LastModifiedAt,
    label: PracticeChartTableFormLabel.LastModifiedAt,
    width: '170px',
    onClick: () => {
      return;
    },
  },
  {
    name: '',
    label: '',
    width: '140px',
  },
];

const PracticeChart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const { openModal } = useModal();

  const [page, setPage] = useState(Number(query.get('page')) || 1);
  const [params, setParams] = useState<TPracticeChartListParams>({
    page,
    size: DEFAULT_PAGE_SIZE,
    searchWord: query.get('searchWord') || undefined,
    chartType: Number(query.get('chartType')) || undefined,
    lastModifiedAt: query.get('lastModifiedAt') || undefined,
    sortBy: (query.get('sortBy') as PracticeChartSortLabelType) || undefined,
    isAsc:
      query.get('sort') === SortDirection.Asc
        ? true
        : query.get('sort') === SortDirection.Desc
        ? false
        : undefined,
  });
  const [sortBy, setSortBy] = useState(query.get('sortBy') || '');
  const [sortDirection, setSortDirection] = useState(query.get('sort') || '');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (sortBy !== query.get('sortBy')) setSortBy('');
    if (sortDirection !== query.get('sort')) setSortDirection('');
    setParams({
      ...params,
      page,
      searchWord: query.get('searchWord') || undefined,
      chartType: Number(query.get('chartType')) || undefined,
      lastModifiedAt: query.get('lastModifiedAt') || undefined,
    });
  }, [location.search, page]);

  const { data: practiceChartListData, isFetching } = useQuery(
    ['getPracticeChartList', params],
    () => getPracticeChartList(params)
  );

  const practiceChartListTableData =
    practiceChartListData?.data.practiceChartList || [];
  const practiceChartListTablePaging = practiceChartListData?.data.paging;

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onClickRegisterChart = () =>
    openModal({
      element: <RegisterPracticeChart />,
    });

  const onClickEdit = (chartId: number, chartName: string) =>
    openModal({
      element: <ChangeChartName chartId={chartId} chartName={chartName} />,
    });

  const onClickRegister = (
    chartId: number,
    chartType: string,
    lastModifiedAt: string
  ) => {
    const chartRoute = ChartInfo.find((chart) => chart.name === chartType)
      ?.route;
    if (!chartRoute) {
      openModal({
        title: '차트 조회 실패',
        text: '관리자에게 문의해 주세요.',
      });
      return;
    }
    if (lastModifiedAt === '-') {
      navigate(`/practice${chartRoute}/${chartId}/register`);
    } else {
      navigate(`/practice${chartRoute}/${chartId}`);
    }
  };

  const renderActions = (
    row: TPracticeChartTableInfo,
    headerIndex?: number
  ) => (
    <>
      {headerIndex === 2 && (
        <div className="flex justify_center gap8">
          {row.chartName}
          <button
            onClick={() => onClickEdit(row.practiceChartId, row.chartName)}
          >
            <img src={PenIcon} width={16} height={16} alt="pen_icon" />
          </button>
        </div>
      )}
      {headerIndex === 4 && (
        <>
          <button
            className={styles.link_s}
            onClick={() =>
              onClickRegister(
                row.practiceChartId,
                row.chartType,
                row.lastModifiedAt
              )
            }
          >
            <span>차트 작성</span>
            <ChevronRightIcon
              fill={STYLES_VARIABLES.PURPLE_01}
              width={18}
              height={18}
            />
          </button>
        </>
      )}
    </>
  );

  const onClickSort = (label: string) => {
    const sortAsc =
      sortDirection === ''
        ? SortDirection.Asc
        : sortDirection === SortDirection.Asc
        ? SortDirection.Desc
        : '';
    const sortBy = {
      [PracticeChartTableSortingLabel.ChartName]:
        PracticeChartSortLabelType.ChartName,
      [PracticeChartTableSortingLabel.LastModifiedAt]:
        PracticeChartSortLabelType.LastModifiedAt,
    }[label as PracticeChartTableSortingLabel];

    setSortDirection(sortAsc);
    setSortBy(sortBy);

    if (page !== 1) {
      setPage(1);
    }
    if (!sortAsc) {
      query.delete('sort');
      query.delete('sortBy');
    } else {
      query.set('sort', sortAsc);
      query.set('sortBy', sortBy);
    }
    navigate({
      search: query.toString(),
    });
    setParams({
      ...params,
      page: 1,
      sortBy: (query.get('sortBy') as PracticeChartSortLabelType) || undefined,
      isAsc:
        sortAsc === SortDirection.Asc
          ? true
          : sortAsc === SortDirection.Desc
          ? false
          : undefined,
    });
  };

  return (
    <div className="min_h100vh flex flex_column">
      <div className="grow_1">
        <Layout bgColor={true} hideFooter={true}>
          <SearchFilter
            isCollective={true}
            input={{
              placeholder: '차트명을 입력해 주세요.',
              width: '488px',
            }}
            dropdown={[
              {
                label: '차트 종류',
                id: 'chartType',
                placeholder: '차트 종류',
                width: '240px',
                useAll: true,
              },
            ]}
            date={{
              label: '최종 작성일',
              id: 'lastModifiedAt',
              width: '240px',
            }}
          />
        </Layout>

        <div className="table_container">
          <div className="flex items_center justify_between mt40 mb24">
            <h2 className="main_title">연습용 차트 목록</h2>
            <button
              className={styles.create_btn}
              onClick={onClickRegisterChart}
            >
              <PlusIcon width={16} height={16} fill={STYLES_VARIABLES.WHITE} />
              <span>차트 생성</span>
            </button>
          </div>
          <TableForm
            data={practiceChartListTableData}
            columns={tableColumns}
            paging={practiceChartListTablePaging}
            sortBy={sortBy}
            sortDirection={sortDirection}
            renderActions={renderActions}
            onClickSort={onClickSort}
            emptyMessage="등록된 연습용 차트가 없습니다."
            isLoading={isLoading}
          />

          {practiceChartListTablePaging &&
            practiceChartListTablePaging.totalElements > 0 && (
              <div className="mt32">
                <Paging
                  activePage={page}
                  totalItemsCount={practiceChartListTablePaging.totalElements}
                  handlePageChange={setPage}
                />
              </div>
            )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PracticeChart;
